'use client';

import { type FC } from 'react';
import { SWRConfig } from 'swr';

import { useDeviceType, Container, ErrorBoundary, Box, Divider } from '@packages/shared';
import { useConfig } from '@packages/utilities';

import { headerDefaultFetcher } from './api';
import { usePaceId } from './usePaceId';
import { AppBanner } from './AppBanner';
import { DesktopNavigation, MobileNavigation } from './Navigation';
import { ResponsiveNavigation } from './Navigation/ResponsiveNavigation';
import { HeaderToolbar } from './Toolbar/HeaderToolbar';
import { DynamicSearchField } from './Toolbar/SearchField';
import { ArrowBox, SlotsDefault, SlotsMinimal } from './Toolbar/Slots';
import { InspiringSearchOnboardingModal } from '../InspiringSearch/InspiringSearchOnboardingModal';
import { Usp } from './Usp';
import { useCopiedDynamicYieldCookie } from './usePaceId/useCopiedDynamicYieldCookie';
import { useHasNewNavigation } from './useHasNewNavigation';
import { ResponsiveHeaderLayout } from './ResponsiveHeaderLayout';
import { LinkedLogo } from './Toolbar/LinkedLogo';

const SEARCH_ICON_MOBILE_RIGHT_POSITION = '123px';

export type HeaderProps = {
  isMinimal?: boolean;
  // withUsp exists only for backwards compatibility for PageLayout in pages folder and root layout in app folder
  withUsp?: boolean;
};

const MinimalHeader: FC = () => (
  // TODO: find out if this is even necessary without navigation in the minimal header, unsure why this default fetcher exists
  <SWRConfig value={{ fetcher: headerDefaultFetcher }}>
    {/* When no inner error boundary catches an error, this is the global boundary for the header. It exists only as backup. When possible, wrap every logical section with a boundary so this one will never show its fallback. */}
    <ErrorBoundary>
      <Container
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          boxShadow: [1, 1, 1, 0],
          px: [0, 0, 0, 1, 0],
        }}
      >
        <hr />
        <HeaderToolbar>
          <ErrorBoundary>
            <SlotsMinimal />
          </ErrorBoundary>
        </HeaderToolbar>
      </Container>

      <Divider sx={{ borderColor: 'grey.main', display: { xs: 'none', lg: 'block' } }} />
    </ErrorBoundary>
  </SWRConfig>
);

/**
 * Integrated header module with appbanner (only mobile), toolbar and navigation.
 * This component is able to get its data whether from client or it accepts the SEO relevant initial data from server to populate SWR cache and to make its further fetches and revalidations on client.
 */
const DefaultHeader: FC<Pick<HeaderProps, 'withUsp'>> = ({ withUsp = true }) => {
  const {
    header: { desktopNavigationMode },
  } = useConfig();
  // NOTE both are false during SSR
  const { isMobile, isDesktop } = useDeviceType();
  const hasNewNavigation = useHasNewNavigation();

  return (
    <SWRConfig value={{ fetcher: headerDefaultFetcher }}>
      <ErrorBoundary>{isMobile && <AppBanner />}</ErrorBoundary>

      <ResponsiveHeaderLayout
        logo={<LinkedLogo />}
        searchField={({ isMobileSearchAlwaysVisible }) =>
          isMobileSearchAlwaysVisible ? (
            <ArrowBox left={`calc(100% - ${SEARCH_ICON_MOBILE_RIGHT_POSITION})`}>
              <DynamicSearchField />
            </ArrowBox>
          ) : (
            <DynamicSearchField />
          )
        }
        icons={() => <SlotsDefault />}
        navigation={({ gridArea }) => (
          <Box sx={{ gridArea, position: 'relative' }}>
            {((isDesktop && hasNewNavigation.desktop) ||
              (!isDesktop && hasNewNavigation.mobile)) && (
              <ResponsiveNavigation mode={desktopNavigationMode} />
            )}
            {isDesktop && !hasNewNavigation.desktop && <DesktopNavigation />}
            {!isDesktop && !hasNewNavigation.mobile && <MobileNavigation />}
          </Box>
        )}
      />

      {isDesktop && withUsp && (
        <Container>
          <ErrorBoundary>
            {/* Later on it might be preferrable to always inject the Usp from outside, or even move it out from the header to the layout, instead of defaulting to the Usp component here. */}
            <Usp />
          </ErrorBoundary>
        </Container>
      )}

      <ErrorBoundary>
        <InspiringSearchOnboardingModal />
      </ErrorBoundary>
    </SWRConfig>
  );
};

export const Header: FC<HeaderProps> = ({ isMinimal, withUsp }) => {
  usePaceId();
  useCopiedDynamicYieldCookie();

  return isMinimal ? <MinimalHeader /> : <DefaultHeader withUsp={withUsp} />;
};
